import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile } from '../types';
import { authService } from '../services/api';
import { ArrowLeft, Edit, User, Briefcase, FileText, Mail } from 'lucide-react';

const ClientProfile: React.FC = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (userProfile) {
          setProfile(userProfile);
        } else {
          // Try to fetch profile from API
          const fetchedProfile = await authService.getProfile();
          setProfile(fetchedProfile);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        // Use basic user info if profile fetch fails
        setProfile({
          email: user?.email || '',
          name: user?.name || '',
          userType: user?.userType || 'client',
          bio: '',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadProfile();
    } else {
      navigate('/login');
    }
  }, [user, userProfile, navigate]);

  if (!user || user.userType !== 'client') {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-white/70 hover:text-white mb-4"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-white">Client Profile</h1>
              <p className="text-white/70 mt-2">
                Your company profile and information
              </p>
            </div>
            <button
              onClick={() => navigate('/edit-profile')}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium"
            >
              <Edit size={16} />
              <span>Edit Profile</span>
            </button>
          </div>
        </div>

        {/* Profile Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile Card */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white flex items-center space-x-2 mb-4">
                <User size={20} />
                <span>Company Information</span>
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Company Name
                  </label>
                  <p className="text-lg text-white">{profile?.name || 'Not specified'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Email
                  </label>
                  <div className="flex items-center space-x-2">
                    <Mail size={16} className="text-white/40" />
                    <p className="text-white">{profile?.email || 'Not specified'}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Account Type
                  </label>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    <Briefcase size={14} className="mr-1" />
                    Client
                  </span>
                </div>
              </div>
            </div>

            {/* Company Description */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white flex items-center space-x-2 mb-4">
                <FileText size={20} />
                <span>Company Description</span>
              </h3>
              
              {profile?.bio ? (
                <p className="text-white/80 leading-relaxed">{profile.bio}</p>
              ) : (
                <div className="text-center py-8">
                  <FileText size={48} className="text-white/30 mx-auto mb-4" />
                  <p className="text-white/60 mb-2">No company description yet</p>
                  <p className="text-sm text-white/40">
                    Add a description to help freelancers understand your company better
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/post-project')}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium"
                >
                  <Briefcase size={16} />
                  <span>Post New Project</span>
                </button>
                
                <button
                  onClick={() => navigate('/client-projects')}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
                >
                  <Briefcase size={16} />
                  <span>View My Projects</span>
                </button>
                
                <button
                  onClick={() => navigate('/edit-profile')}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-white/20 text-white hover:bg-white/10 rounded-md font-medium"
                >
                  <Edit size={16} />
                  <span>Edit Profile</span>
                </button>
              </div>
            </div>

            {/* Profile Stats */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">Profile Status</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/60">Profile Complete</span>
                  <span className={`text-sm font-medium ${
                    profile?.bio ? 'text-green-400' : 'text-yellow-400'
                  }`}>
                    {profile?.bio ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/60">Last Updated</span>
                  <span className="text-sm text-white/40">
                    {profile?.bio ? 'Recently' : 'Never'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;
